import { FileWithPath } from 'react-dropzone'
import slugify from 'slugify'
import { NativeFileWithSanitizedNameAndPath } from '../../../../../../features/media'
import { useState } from 'react'

interface NativeFileWithSanitizedNameAndDataUrl extends NativeFileWithSanitizedNameAndPath {
  dataUrl: string
}

function sanitizeDroppedFiles(droppedFiles: FileWithPath[]): NativeFileWithSanitizedNameAndPath[] {
  return droppedFiles.map(file => {
    const sanitizedFilename = sanitizeFilename(file.name)
    const sanitizedRelativePath = sanitizeRelativePath(file.relativePath || file.name)

    return { fileData: file, sanitizedFilename, sanitizedRelativePath }
  })
}

const sanitizeFilename = (filename: string) => {
  const filenameWithoutExtension = filename.replace(/\.[^/.]+$/, '')
  const filenameSlugified = filename.replace(
    filenameWithoutExtension,
    slugify(filenameWithoutExtension)
  )

  return filenameSlugified
}

const sanitizeRelativePath = (relativePath: string) => {
  const relativePathWithoutExtension = relativePath.replace(/\.[^/.]+$/, '')
  const relativePathSlugified = relativePath.replace(
    relativePathWithoutExtension,
    slugifyKeepSlash(relativePathWithoutExtension)
  )

  return relativePathSlugified
}

export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export const useReadyToUploadFiles = () => {
  const [readyFiles, setReadyFiles] = useState<NativeFileWithSanitizedNameAndDataUrl[]>([])

  const clearReadyFiles = () => {
    setReadyFiles([])
  }

  const handleDrop = async (droppedFiles: FileWithPath[]) => {
    const sanitizedFiles = sanitizeDroppedFiles(droppedFiles)
    const files = await Promise.all<NativeFileWithSanitizedNameAndDataUrl>(
      sanitizedFiles.map(async ({ sanitizedFilename, sanitizedRelativePath, fileData }) => ({
        sanitizedFilename,
        sanitizedRelativePath,
        fileData,
        dataUrl: await fileToDataUrl(fileData)
      }))
    )
    setReadyFiles([...readyFiles, ...files])
  }

  return { readyFiles, handleDrop, clearReadyFiles }
}

function slugifyKeepSlash(text: string) {
  return text
    .trim()
    .replace(/[^A-Za-z0-9\/]+/g, '-') // replace everything except letters, numbers, and "/"
    .replace(/-+/g, '-') // collapse multiple "-"
    .replace(/^-|-$/g, '') // trim "-" from start/end
}
